import clsx from 'clsx';
import { Avatar, Icon, InputCheckbox } from 'ui';
import InputPoints from 'core/modules/atoms/InputPoints';
import InputPointsNumericScale from 'core/modules/atoms/InputPointsNumericScale';
import InputPointsQualitativeScale from 'core/modules/atoms/InputPointsQualitativeScale/InputPointsQualitativeScale';
import Score from 'core/modules/atoms/Score';
import StatusIcon from 'core/modules/atoms/StatusIcon/StatusIcon';
import { useGradeSaveStatus } from 'core/modules/components/lists/users/StudentListStatus/GradeSaveStatusContext';
import { isScaleNumeric, isScaleQualitative } from 'core/utils/constants/evaluation';
import { cleanEmail, getStudentDisplayName } from 'core/utils/user';
import { roundNumber, successResponse } from 'core/utils/utils';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Referencias estables: son dependencias de useCallback
const EMPTY_ITEM = {};
const noopAsync = () => Promise.resolve();

// Extraído de UserItem para bajar su complejidad cognitiva (Sonar S3776)
function UserItemPointsInput({
  colorsPoints,
  disabled,
  evaluationScale,
  isLate,
  isScoreReadOnly,
  onChangeError,
  onInputChange,
  onQualitativeChange,
  score,
  scoreScale,
  showDotted,
  t,
}) {
  const hasNoScore = score === null;

  if (isScaleNumeric(evaluationScale)) {
    return (
      <InputPointsNumericScale
        colorsPoints={colorsPoints}
        disabled={disabled}
        showDotted={showDotted}
        isLate={isLate}
        min={evaluationScale?.min || 0}
        max={evaluationScale?.max}
        evaluationScale={evaluationScale}
        isScoreReadOnly={isScoreReadOnly}
        value={hasNoScore ? '-' : score}
        onChange={onInputChange}
        changeError={onChangeError}
        numberDecimals={evaluationScale?.number_decimals}
      />
    );
  }

  if (isScaleQualitative(evaluationScale)) {
    return (
      <InputPointsQualitativeScale
        t={t}
        colorsPoints={colorsPoints}
        disabled={disabled}
        showDotted={showDotted}
        isLate={isLate}
        evaluationScale={evaluationScale}
        isScoreReadOnly={isScoreReadOnly}
        value={hasNoScore ? '-' : score}
        onChange={onQualitativeChange}
      />
    );
  }

  return (
    <InputPoints
      scoreScale={scoreScale}
      colorsPoints={colorsPoints}
      disabled={disabled}
      showDotted={showDotted}
      isLate={isLate}
      isScoreReadOnly={isScoreReadOnly}
      value={hasNoScore ? '-' : roundNumber(score, 2)}
      onChange={onInputChange}
      max={scoreScale > 0 ? scoreScale : undefined}
    />
  );
}

UserItemPointsInput.propTypes = {
  colorsPoints: PropTypes.object,
  disabled: PropTypes.bool,
  evaluationScale: PropTypes.object,
  isLate: PropTypes.bool,
  isScoreReadOnly: PropTypes.bool,
  onChangeError: PropTypes.func,
  onInputChange: PropTypes.func,
  onQualitativeChange: PropTypes.func,
  score: PropTypes.number,
  scoreScale: PropTypes.number,
  showDotted: PropTypes.bool,
  t: PropTypes.func,
};

const UserItem = ({
  guid,
  name,
  lastName,
  lastname,
  email,
  thumbnail,
  score,
  checkboxsRight,
  showPoints = true,
  showCheckbox = true,
  checked,
  isScore = false,
  isScoreReadOnly,
  isBooleanScore,
  isLate,
  iconAction,
  provisionalScore = false,
  onClick,
  onChange,
  onAction,
  activeUser,
  onScoreChange = noopAsync,
  shouldSelectOnInput = false,
  sizeAvatar = 'sm',
  customRight,
  warningLeft,
  scoreScale,
  colorsPoints,
  disabledInputPoints = false,
  item = EMPTY_ITEM,
  evaluationScale,
  waitReturn = false,
  showLastNameFirst,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [changeErrorPoints, setChangeErrorPoints] = useState(false);
  const { t } = useTranslation();
  const { onSaveCancel, onSaveEnd, onSaveStart } = useGradeSaveStatus();
  const isWaitingDebounceRef = useRef(false);

  const displayName = getStudentDisplayName(name, lastName, showLastNameFirst, lastname);

  const handleScoreChange = useCallback(
    async (data) => {
      isWaitingDebounceRef.current = false;
      let hasSucceeded = false;
      try {
        const response = await onScoreChange(data);
        // El interceptor de axios resuelve los errores como { error }: solo cuenta la respuesta de éxito.
        hasSucceeded = Boolean(response) && successResponse(response);
        return response;
      } finally {
        setIsLoading(false);
        onSaveEnd(hasSucceeded);
      }
    },
    [onScoreChange, onSaveEnd]
  );

  const [debouncedUpdateScore] = useState(() => debounce(handleScoreChange, 1000));

  useEffect(() => {
    return () => {
      debouncedUpdateScore.cancel();
      if (isWaitingDebounceRef.current) onSaveCancel();
      setIsLoading(false);
    };
  }, [debouncedUpdateScore, onSaveCancel]);

  const handleInputChange = useCallback(
    (params) => {
      setIsLoading(true);
      // Una ráfaga de pulsaciones sale como un solo guardado: solo la primera lo abre.
      if (!isWaitingDebounceRef.current) {
        isWaitingDebounceRef.current = true;
        onSaveStart();
      }
      debouncedUpdateScore({ ...item, score: params });
    },
    [debouncedUpdateScore, item, onSaveStart]
  );

  const handleQualitativeChange = useCallback(
    async (params) => {
      setIsLoading(true);
      onSaveStart();
      await handleScoreChange({ ...item, score: params });
    },
    [handleScoreChange, item, onSaveStart]
  );

  const showLoading = isLoading && waitReturn;

  function selectActiveUser() {
    onClick(activeUser);
  }

  const selectOnInputProps = shouldSelectOnInput ? { onClick: selectActiveUser } : {};

  return (
    <div
      id={'user-item-' + guid}
      className={clsx('user-item', {
        'user-item--checkboxs-right': checkboxsRight,
        selected: activeUser && activeUser.guid === guid,
        'user-item--checked': checked,
        'user-item--without-points': !showPoints,
      })}
    >
      <div className="user-item__container">
        <div className="user-item__main-info">
          {warningLeft && <div className="user-item__warning-left">{warningLeft}</div>}
          {showCheckbox && (
            <div className="user-item__checkbox-wrapper">
              <div className={clsx('user-item__input-checkbox', { 'user-item__input-checkbox--hidden': showLoading })}>
                <InputCheckbox
                  checked={checked}
                  disabled={showLoading}
                  // Hidden label gives the checkbox an accessible name. The visible name below is
                  // not reused as the label because clicking it opens the user, not toggles the checkbox.
                  label={<span className="sr-only">{displayName}</span>}
                  onChange={(e) => {
                    onChange(e, guid);
                    onClick(activeUser);
                  }}
                />
              </div>
              <div className={clsx('user-item__loading', { 'user-item__loading--visible': showLoading })}>
                <div className="user-item__loading-spinner" />
              </div>
            </div>
          )}
          <div className="user-item__avatar-name" {...selectOnInputProps}>
            <div className="user-item__avatar">
              <Avatar image={thumbnail} size={sizeAvatar} />
            </div>
            <div className="user-item__texts">
              <span className="user-item__name">{displayName}</span>
              <span className="user-item__email"> {cleanEmail(email)}</span>
            </div>
          </div>
        </div>

        {showPoints && (
          <div className="user-item__input-points" {...selectOnInputProps}>
            <UserItemPointsInput
              colorsPoints={colorsPoints}
              disabled={disabledInputPoints}
              evaluationScale={evaluationScale}
              isLate={isLate}
              isScoreReadOnly={isScoreReadOnly}
              onChangeError={setChangeErrorPoints}
              onInputChange={handleInputChange}
              onQualitativeChange={handleQualitativeChange}
              score={score}
              scoreScale={scoreScale}
              showDotted={provisionalScore}
              t={t}
            />
          </div>
        )}
        {isScore && <Score score={score} scoreScale={scoreScale} evaluationScale={evaluationScale} />}
        {onAction && (
          <div className="user-item__action" onClick={() => onAction(guid)}>
            <Icon name={iconAction} />
          </div>
        )}
        {isBooleanScore && score !== null && (
          <div className="user-item__boolean-score">
            <div className="user-item__boolean-score-icon">
              <StatusIcon
                iconName={score === 0 ? 'incorrect-circle' : 'done-circle'}
                label={t(score === 0 ? 'common:Incorrect' : 'common:Correct')}
                size="xl"
              />
            </div>
            {isLate && (
              <div className="input-points__late">
                <StatusIcon iconName="exclamation-circle" label={t('lesson:Delivered late')} shouldShowTooltip />
              </div>
            )}
          </div>
        )}
        {customRight}
      </div>
      {changeErrorPoints && (
        <div className="input-points__error-message">
          {t('assigments:The score must be between min and max', { min: evaluationScale?.min, max: evaluationScale?.max })}
        </div>
      )}
    </div>
  );
};

UserItem.propTypes = {
  activeUser: PropTypes.object,
  waitReturn: PropTypes.bool,
  checkboxsRight: PropTypes.bool,
  checked: PropTypes.bool,
  colorsPoints: PropTypes.object,
  customRight: PropTypes.node,
  warningLeft: PropTypes.node,
  disabledInputPoints: PropTypes.bool,
  email: PropTypes.string,
  feedbackQuestionType: PropTypes.string,
  guid: PropTypes.string,
  iconAction: PropTypes.string,
  isBooleanScore: PropTypes.bool,
  isFeedbackActivity: PropTypes.bool,
  isLate: PropTypes.bool,
  isScore: PropTypes.bool,
  isScoreReadOnly: PropTypes.bool,
  item: PropTypes.object,
  lastName: PropTypes.string,
  lastname: PropTypes.string,
  name: PropTypes.string,
  note: PropTypes.number,
  onAction: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onScoreChange: PropTypes.func,
  provisionalScore: PropTypes.bool,
  score: PropTypes.number,
  scoreScale: PropTypes.number,
  shouldSelectOnInput: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  showLastNameFirst: PropTypes.bool,
  showPoints: PropTypes.bool,
  sizeAvatar: PropTypes.oneOf(['sm', 'md']),
  thumbnail: PropTypes.string,
  evaluationScale: PropTypes.object,
};

export default UserItem;
